<script lang="ts">
	import { browser } from '$app/env';
	import { goto } from '$app/navigation';
	import { page } from '$app/state';
	import { buildBarcosPath, comunidadForProvince, type BarcosRoute } from '$lib/geo';
	import { Button } from '$lib/components/ui/button/index.js';
	import * as Pagination from '$lib/components/ui/pagination';
	import * as Popover from '$lib/components/ui/popover';
	import { Separator } from '$lib/components/ui/separator';
	import { Skeleton } from '$lib/components/ui/skeleton';
	import { getListingTypeFilterLabel } from '$lib/listing-display';
	import { minAvailableOrLatestRangePrice } from '$lib/listing-pricing/calculate';
	import CardNew from '@/components/Cards/CardNew.svelte';
	import { localizeHref } from '@/paraglide/runtime';
	import {
		Collections,
		type ListingDateRangesResponse,
		type ListingsResponse
	} from '@/pocketbase/pocketbase-types';
	import { getMarketplaceFilters } from '@/stores/MarketplaceFilters.svelte';
	import { pb } from '@/utils/ApiClient';
	import ChevronLeft from '@lucide/svelte/icons/chevron-left';
	import ChevronRight from '@lucide/svelte/icons/chevron-right';
	import ChevronsUpDown from '@lucide/svelte/icons/chevrons-up-down';
	import MapPin from '@lucide/svelte/icons/map-pin';
	import Sailboat from '@lucide/svelte/icons/sailboat';
	import Search from '@lucide/svelte/icons/search';
	import { onMount } from 'svelte';
	import { SvelteMap } from 'svelte/reactivity';

	let {
		route = null,
		cityName = null,
		portId = null,
		portName = null,
		initialListings = [],
		initialTotalItems = 0,
		initialTotalPages = 0
	}: {
		route?: BarcosRoute | null;
		cityName?: string | null;
		portId?: string | null;
		portName?: string | null;
		initialListings?: ListingsResponse[];
		initialTotalItems?: number;
		initialTotalPages?: number;
	} = $props();

	const filters = getMarketplaceFilters();

	const locationLabel = $derived.by(() => {
		if (!route) return null;
		if (portName) return portName;
		if (cityName) return cityName;
		if (route.comunidad && !route.comunidad.uniprovincial && route.provincia) {
			return route.provincia;
		}
		return route.comunidad?.name ?? null;
	});

	const heading = $derived(() => {
		const what =
			route?.tipo === 'experiencia'
				? 'Experiencias náuticas'
				: route?.tipo === 'alquiler'
					? 'Alquiler de barcos'
					: 'Alquileres y experiencias náuticas';
		return locationLabel ? `${what} en ${locationLabel}` : what;
	});

	let currentPage = $state(1);
	let perPage = $state(16);
	let totalItems = $state(initialTotalItems);
	let totalPages = $state(initialTotalPages);
	let isLoading = $state(false);
	let popoverOpen = $state(false);
	let listings = $state<(ListingsResponse & { minPriceFromRanges?: number | null })[]>([
		...initialListings
	]);

	const perPageOptions = [
		{ label: '8 por página', value: '8' },
		{ label: '12 por página', value: '12' },
		{ label: '16 por página', value: '16' },
		{ label: '24 por página', value: '24' },
		{ label: '48 por página', value: '48' }
	];

	const subtitle = $derived(() => {
		const tipoLabel = getListingTypeFilterLabel(filters.listingType, {
			experiencesOnly: filters.experiencesOnly
		});
		const base =
			totalItems > 0
				? `${totalItems} anuncios disponibles`
				: 'No hay anuncios que coincidan con tu búsqueda';
		const location = locationLabel ? ` en ${locationLabel}` : '';
		const tipo = tipoLabel ? ` · ${tipoLabel}` : '';
		return `${base}${location}${tipo}`;
	});

	// Evita que el $effect dispare una carga antes de sincronizar los filtros
	// con la URL (carrera que mostraba resultados sin filtrar).
	let initialized = $state(false);

	function initializeFromURL() {
		if (!browser) return;

		// window.location en lugar de page.url para no crear dependencias
		// reactivas sobre los query params (updateURL los reescribe).
		const params = new URLSearchParams(window.location.search);

		const pageParam = params.get('page');
		const perPageParam = params.get('perPage');

		if (pageParam) currentPage = parseInt(pageParam);
		if (perPageParam) perPage = parseInt(perPageParam);

		// Primero la ubicación del path, después los query params (legacy).
		filters.setRouteFromPath(
			route,
			cityName,
			portId && portName ? { id: portId, name: portName } : null
		);
		filters.fromURLParams(params);
	}

	$effect(() => {
		if (!browser || !initialized) return;

		filters.onFiltersChange(() => {
			currentPage = 1;
			updateURL();
			loadListings();
		});

		loadListings();

		return () => {
			filters.destroy();
		};
	});

	// Descarta respuestas de cargas obsoletas (la última petición gana).
	let loadSeq = 0;

	async function loadListings() {
		const seq = ++loadSeq;
		isLoading = true;
		try {
			const filterQuery = filters.buildFilterQuery();
			const response = await pb
				.collection(Collections.Listings)
				.getList<ListingsResponse>(currentPage, perPage, {
					sort: '-created',
					expand: 'boat.port',
					filter: filterQuery,
					requestKey: null
				});

			if (seq !== loadSeq) return;

			listings = response.items;
			totalItems = response.totalItems;
			totalPages = response.totalPages;

			if (response.items.length > 0) {
				const ids = response.items.map((listing) => listing.id);
				const rangesFilter = ids.map((id) => `listing = "${id}"`).join(' || ');
				const ranges = await pb
					.collection(Collections.ListingDateRanges)
					.getFullList<ListingDateRangesResponse>({
						filter: `(${rangesFilter})`,
						requestKey: null
					});

				if (seq !== loadSeq) return;

				const rangesByListing = new SvelteMap<string, ListingDateRangesResponse[]>();

				for (const range of ranges) {
					const listingRanges = rangesByListing.get(range.listing) ?? [];
					listingRanges.push(range);
					rangesByListing.set(range.listing, listingRanges);
				}

				listings = response.items.map((listing) => ({
					...listing,
					minPriceFromRanges: minAvailableOrLatestRangePrice(rangesByListing.get(listing.id) ?? [])
				}));
			}
		} catch (error) {
			console.error('Error loading listings:', error);
		} finally {
			if (seq === loadSeq) {
				isLoading = false;
			}
		}
	}

	function updateURL() {
		if (!browser) return;

		const urlParams = filters.toURLParams();
		urlParams.set('page', currentPage.toString());
		urlParams.set('perPage', perPage.toString());

		goto(`${page.url.pathname}?${urlParams.toString()}`, { replaceState: true });
	}

	async function handlePerPageChange(value: string) {
		perPage = parseInt(value);
		currentPage = 1;
		updateURL();
		popoverOpen = false;
		loadListings();
	}

	async function handlePageChange(newPage: number) {
		currentPage = newPage;
		updateURL();
		loadListings();
	}

	onMount(() => {
		initializeFromURL();
		initialized = true;
	});
</script>

<main class="grid lg:grid-cols-5">
	<div class="col-span-3 lg:col-span-5">
		<div class="h-full px-4 md:py-6 lg:px-8">
			<section class="h-full space-y-6" aria-label="Marketplace náutico">
				<article class="border-none p-0 outline-hidden">
					<div class="flex items-center justify-between gap-4">
						<div class="min-w-0 space-y-1">
							<h1 class="text-lg leading-snug font-semibold tracking-tight md:text-2xl">
								{heading()}
							</h1>
							<p class="text-muted-foreground text-sm">{subtitle()}</p>
						</div>

						<div class="hidden shrink-0 items-center space-x-2 md:flex">
							<Popover.Root bind:open={popoverOpen}>
								<Popover.Trigger>
									<Button variant="outline" class="w-[180px] justify-between" type="button">
										{perPage} por página
										<ChevronsUpDown class="ml-2 h-4 w-4 shrink-0 opacity-50" />
									</Button>
								</Popover.Trigger>
								<Popover.Content class="w-[180px] p-0">
									<div class="flex flex-col">
										{#each perPageOptions as option (option.value)}
											<button
												type="button"
												class="cursor-pointer px-4 py-2 text-sm hover:bg-gray-100"
												onclick={() => handlePerPageChange(option.value)}
											>
												{option.label}
											</button>
										{/each}
									</div>
								</Popover.Content>
							</Popover.Root>
						</div>
					</div>
					<Separator class="my-4" />

					{#if isLoading && listings.length === 0}
						<div class="grid grid-cols-2 gap-4 xl:grid-cols-4">
							{#each Array(perPage) as _, i (i)}
								<div class="flex flex-col space-y-4">
									<Skeleton class="aspect-portrait w-full rounded-md" />
									<div class="space-y-2">
										<Skeleton class="h-4 w-full" />
										<Skeleton class="h-4 w-3/4" />
									</div>
								</div>
							{/each}
						</div>
					{:else if !isLoading && listings.length === 0}
						<div class="flex min-h-[400px] items-center justify-center py-12">
							<div class="mx-auto max-w-2xl px-4 text-center">
								<Sailboat class="text-primary/60 mx-auto mb-6 h-16 w-16" />
								<h2 class="text-foreground mb-3 text-2xl font-bold">
									No hay anuncios con estos filtros
								</h2>
								<p class="text-muted-foreground mb-8 text-base">
									Prueba otro tipo de salida, amplía el rango de fechas o explora otras zonas.
								</p>
								<Button
									size="lg"
									variant="outline"
									type="button"
									class="mx-auto"
									onclick={() => {
										filters.reset();
										goto(localizeHref('/barcos'), { replaceState: true });
									}}
								>
									<Search class="mr-2 h-5 w-5" />
									Ver todos los anuncios
								</Button>
								{#if locationLabel}
									<div class="mt-8 border-t pt-6">
										<p class="text-muted-foreground mb-3 text-sm">Otras zonas populares:</p>
										<div class="flex flex-wrap justify-center gap-2">
											{#each ['Mallorca', 'Ibiza', 'Barcelona', 'Valencia', 'Alicante'] as suggestedProvince (suggestedProvince)}
												{#if suggestedProvince.toLowerCase() !== locationLabel?.toLowerCase()}
													<Button
														variant="secondary"
														size="sm"
														href={localizeHref(
															buildBarcosPath({
																tipo: route?.tipo,
																comunidad: comunidadForProvince(suggestedProvince),
																provincia: suggestedProvince
															})
														)}
													>
														<MapPin class="mr-1 h-3 w-3" />
														{suggestedProvince}
													</Button>
												{/if}
											{/each}
										</div>
									</div>
								{/if}
							</div>
						</div>
					{:else}
						<div class="relative grid grid-cols-2 gap-x-1 gap-y-4 md:gap-4 xl:grid-cols-4">
							{#each listings as listing (listing.id)}
								<CardNew
									listing={listing}
									minPriceFromRanges={listing.minPriceFromRanges}
									aspectRatio="portrait"
									width={300}
									height={400}
								/>
							{/each}
						</div>
					{/if}

					{#if totalPages > 1 && listings.length > 0}
						<nav class="mt-8 flex justify-center" aria-label="Paginación del marketplace">
							<Pagination.Root count={totalItems} perPage={perPage} bind:page={currentPage}>
								<Pagination.Content>
									<Pagination.Item>
										<Pagination.PrevButton
											onclick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
										>
											<ChevronLeft class="size-4" />
											Anterior
										</Pagination.PrevButton>
									</Pagination.Item>
									{#each Array(totalPages) as _, i (i)}
										<Pagination.Item>
											<Pagination.Link
												page={{ value: i + 1, type: 'page' }}
												isActive={currentPage === i + 1}
												onclick={() => handlePageChange(i + 1)}
											/>
										</Pagination.Item>
									{/each}
									<Pagination.Item>
										<Pagination.NextButton
											onclick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
										>
											Siguiente
											<ChevronRight class="size-4" />
										</Pagination.NextButton>
									</Pagination.Item>
								</Pagination.Content>
							</Pagination.Root>
						</nav>
					{/if}
				</article>
			</section>
		</div>
	</div>
</main>
